import generateNumber from '../../../utils/generateNumber';
import { getDeviceCategory } from '../../../utils/getDeviceCategory';
import type { Widget } from '../../types/Widget';
import { WidgetType } from '../../types/WidgetType';
import { isServerside } from '../../utils/isServerside';
import type Localiser from '../Localiser';
import { Livingetc } from '../sites/definitions/Livingetc';
import { MarieClaireUK } from '../sites/definitions/MarieClaireUK';
import { MarieClaireUS } from '../sites/definitions/MarieClaireUS';
import { MyImperfectLife } from '../sites/definitions/MyImperfectLife';
import { WhoWhatWear } from '../sites/definitions/WhoWhatWear';
import { WomanAndHome } from '../sites/definitions/WomanAndHome';
import type { Site } from '../sites/Site';

import BaseFeatures from './BaseFeatures/BaseFeatures';
import hasValue from './BaseFeatures/utils/hasValue';
import parseBoolean from './BaseFeatures/utils/parseBoolean';
import { getLabelAPIParams } from './getLabelFeatures';
import FeatureId from './types/FeatureId';
import FeatureType from './types/FeatureType';
import FeatureValue from './types/FeatureValue';

export default class extends BaseFeatures {
  private readonly origin: string;

  constructor(
    attributes: Record<string, string | undefined>,
    widget: Widget,
    editorial: boolean,
    localiser: Localiser,
    site: Site,
    territory?: string,
    keywords?: string[],
    origin?: string,
  ) {
    super(attributes, widget, editorial, localiser, site, territory, keywords);
    this.origin = origin || (isServerside() ? 'widgets-nodejs' : 'widgets-clientside');

    this.features = [
      {
        id: FeatureId.MODEL_NAME,
        type: FeatureType.TEXT,
        value: FeatureValue.MODEL_NAME,
      },
      {
        id: FeatureId.TENANCY,
        type: FeatureType.TEXT,
        value: FeatureValue.TENANCY,
      },
      {
        id: FeatureId.ARTICLE_TYPE,
        type: FeatureType.TEXT,
        value: FeatureValue.ARTICLE_TYPE,
      },
      {
        id: FeatureId.ARTICLE_ID,
        type: FeatureType.TEXT,
        value: FeatureValue.ARTICLE_ID,
      },
      {
        id: FeatureId.LANGUAGE,
        type: FeatureType.TEXT,
        value: FeatureValue.LANGUAGE,
        getDefaultValue: (): string => {
          const { localiser } = this;

          return localiser.language;
        },
      },
      {
        id: FeatureId.SITE,
        type: FeatureType.TEXT,
        value: FeatureValue.SITE,
        getDefaultValue: (): string => {
          return this.site.id;
        },
      },
      {
        id: FeatureId.AREA,
        type: FeatureType.TEXT,
        value: FeatureValue.AREA,
      },
      {
        id: FeatureId.NOW,
        type: FeatureType.TEXT,
        value: FeatureValue.NOW,
      },
      {
        id: FeatureId.ALLOW_OUT_OF_STOCK,
        type: FeatureType.BOOL,
        value: FeatureValue.ALLOW_OUT_OF_STOCK,
      },
      {
        id: FeatureId.COMBINE_PRODUCT_TYPES,
        type: FeatureType.BOOL,
        value: FeatureValue.COMBINE_PRODUCT_TYPES,
        getDefaultValue: (): boolean | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.EAGLE:
            case WidgetType.MERCHANT_OVERVIEW:
            case WidgetType.MOST_POPULAR_OFFERS:
            case WidgetType.TILE:
            case WidgetType.STREAMING:
            case WidgetType.SCROLLABLE_TILE:
              return true;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.CACHE_BUST,
        type: FeatureType.TEXT,
        value: FeatureValue.CACHE_BUST,
      },
      {
        id: FeatureId.MODEL_ID,
        type: FeatureType.NUMBER,
        value: FeatureValue.MODEL_ID,
      },
      {
        id: FeatureId.MATCH_ID,
        type: FeatureType.NUMBER,
        value: FeatureValue.MATCH_ID,
      },
      {
        id: FeatureId.CUSTOM_TRACKING_ID,
        type: FeatureType.TEXT,
        value: FeatureValue.CUSTOM_TRACKING_ID,
      },
      {
        id: FeatureId.MERCHANTS,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_MERCHANTS,
      },
      {
        id: FeatureId.MERCHANT,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_MERCHANT_NAME,
      },
      {
        id: FeatureId.CURRENT_MINUTES,
        type: FeatureType.NUMBER,
        value: FeatureValue.CURRENT_MINUTES,
      },
      {
        id: FeatureId.BATTLE,
        type: FeatureType.TEXT,
        value: FeatureValue.BATTLE,
      },
      {
        id: FeatureId.DATA,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_DATA,
      },
      {
        id: FeatureId.ALL_FILTERS,
        type: FeatureType.BOOL,
        value: FeatureValue.ALL_FILTERS,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.SPECS:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.EXCLUDE_UNLABELLED,
        type: FeatureType.BOOL,
        value: FeatureValue.EXCLUDE_UNLABELLED,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.SPECS:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.INCLUDE_SPECS,
        type: FeatureType.BOOL,
        value: FeatureValue.INCLUDE_SPECS,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.SPECS:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.TEXTS,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_TEXTS,
      },
      {
        id: FeatureId.MONTHLY_COST,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_MONTHLY_COST,
      },
      {
        id: FeatureId.PRICE_MIN,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_MIN_PRICE,
      },
      {
        id: FeatureId.PRICE_MAX,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_MAX_PRICE,
      },
      {
        id: FeatureId.MINUTES,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_MINUTES,
      },
      {
        id: FeatureId.CONTRACT_LENGTH,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_CONTRACT_LENGTH,
      },
      {
        id: FeatureId.UPGRADES,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_UPGRADE,
      },
      {
        id: FeatureId.WASPRICE,
        type: FeatureType.BOOL,
        value: FeatureValue.FILTER_WAS_PRICE,
      },
      {
        id: FeatureId.POSTCODE,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_POSTCODE,
      },
      {
        id: FeatureId.INCLUDES,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_INCLUDES,
      },
      {
        id: FeatureId.SPEED,
        type: FeatureType.NUMBER,
        value: FeatureValue.FILTER_SPEED,
      },
      {
        id: FeatureId.TYPE,
        type: FeatureType.TEXT,
        value: FeatureValue.FILTER_TYPE,
      },
      {
        id: FeatureId.SORT,
        type: FeatureType.LIST,
        value: FeatureValue.SORT,
        label: 'Sort',
        battle: true,
        options: [
          {
            value: 'recommended',
            formatted_value: 'Recommended',
          },
          {
            value: 'name',
            formatted_value: 'Product Name',
          },
          {
            value: 'price',
            formatted_value: 'Price',
          },
          {
            value: 'merchant',
            formatted_value: 'Merchant Name',
          },
          {
            value: 'monthly_cost',
            formatted_value: 'Monthly Cost',
          },
          {
            value: 'upfront_cost',
            formatted_value: 'Upfront Cost',
          },
          {
            value: 'all_prices',
            formatted_value: 'Sort by Monthly and Upfront Price',
          },
          {
            value: 'deal_end_time',
            formatted_value: 'Deal End Time',
          },
          {
            value: 'deal_start_time',
            formatted_value: 'Deal Start Time',
          },
          {
            value: 'was_price_ratio',
            formatted_value: 'Was Price Ratio',
          },
          {
            value: 'contract_speed',
            formatted_value: 'Speed',
          },
          {
            value: 'contract_length',
            formatted_value: 'Contract Length',
          },
          {
            value: 'data',
            formatted_value: 'Data',
          },
          {
            value: 'minutes',
            formatted_value: 'Minutes',
          },
          {
            value: 'texts',
            formatted_value: 'Texts',
          },
          {
            value: 'network',
            formatted_value: 'Network Name',
          },
          {
            value: 'random',
            formatted_value: 'Random',
          },
          {
            value: 'money_saving',
            formatted_value: 'Money Saving',
          },
          {
            value: 'percentage_saving',
            formatted_value: 'Percentage Saving',
          },
          {
            value: 'offer_saving',
            formatted_value: 'Offer Saving',
          },
          {
            value: 'voucher',
            formatted_value: 'Voucher',
          },
        ],
        getDefaultValue: (): string | null => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.MERCHANT_OVERVIEW:
            case WidgetType.EAGLE:
            case WidgetType.TILE:
              return 'voucher';
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DEAL_START_TIME_FROM,
        type: FeatureType.DATE_TIME,
        value: FeatureValue.DEAL_START_TIME_FROM,
      },
      {
        id: FeatureId.DEAL_START_TIME_TO,
        type: FeatureType.DATE_TIME,
        value: FeatureValue.DEAL_START_TIME_TO,
      },
      {
        id: FeatureId.DEAL_END_TIME_FROM,
        type: FeatureType.DATE_TIME,
        value: FeatureValue.DEAL_END_TIME_FROM,
      },
      {
        id: FeatureId.DEAL_END_TIME_TO,
        type: FeatureType.DATE_TIME,
        value: FeatureValue.DEAL_END_TIME_TO,
      },
      {
        id: FeatureId.RAND,
        type: FeatureType.NUMBER,
        value: FeatureValue.RAND,
        getDefaultValue: (): number | null => {
          const { attributes } = this;

          if (attributes.sort === 'random') {
            // Send the rand parameter when using random sort to ensure we don't always hit
            // the same cached response
            // The number of cache entries will be equal to the value passed to generateNumber
            return generateNumber(100);
          }

          return null;
        },
      },
      {
        id: FeatureId.DESC,
        type: FeatureType.NUMBER,
        value: FeatureValue.DESC,
        label: 'Sort Descending',
        battle: true,
      },
      {
        id: FeatureId.OFFSET,
        type: FeatureType.NUMBER,
        value: FeatureValue.OFFSET,
      },
      {
        id: FeatureId.PRESERVE_ALL_MATCHES,
        label: 'Preserve All Matches',
        type: FeatureType.BOOL,
        value: FeatureValue.PRESERVE_ALL_MATCHES,
        battle: true,
      },
      {
        id: FeatureId.DEALS_PER_MODEL,
        label: 'Deals per Model',
        type: FeatureType.NUMBER,
        value: FeatureValue.DEALS_PER_MODEL,
        battle: true,
        getDefaultValue: (): number | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.ANF_MULTIMODEL:
            case WidgetType.PARTNER:
            case WidgetType.PPC:
              return 1;
            case WidgetType.TABLETOP:
            case WidgetType.ANF_TABLETOP:
              return 3;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DISTINCT_MERCHANTS,
        label: 'Distinct Merchants',
        type: FeatureType.LIST,
        value: FeatureValue.DISTINCT_MERCHANTS,
        battle: true,
        options: [
          {
            value: 'default',
            formatted_value: 'Distinct Merchants',
          },
          {
            value: 'besteffort',
            formatted_value: 'Distinct Merchants Best Effort (Multimodelreview)',
          },
          {
            value: 'natural',
            formatted_value: 'Distinct Merchants Natural',
          },
        ],
        getDefaultValue: (): string | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.PARTNER:
            case WidgetType.PPC:
              return 'besteffort';
            case WidgetType.EAGLE:
              return 'natural';
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DISTINCT_NETWORKS,
        label: 'Distinct Networks',
        type: FeatureType.BOOL,
        value: FeatureValue.DISTINCT_NETWORKS,
        battle: true,
        getDefaultValue: (): boolean | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.COMPARISON:
              return false;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DISTINCT_LABEL,
        label: 'Distinct Label',
        type: FeatureType.TEXT,
        value: FeatureValue.DISTINCT_LABEL,
      },
      {
        id: FeatureId.MULTI,
        label: 'Multi Model Order',
        type: FeatureType.BOOL,
        value: FeatureValue.MULTI,
        battle: true,
        getDefaultValue: (): boolean | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.COMPARISON:
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.TABLETOP:
            case WidgetType.ANF_TABLETOP:
            case WidgetType.PARTNER:
            case WidgetType.PPC:
            case WidgetType.SPECS:
              return true;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.MULTI_SIMILAR,
        label: 'Multi Similar',
        type: FeatureType.BOOL,
        value: FeatureValue.MULTI_SIMILAR,
        battle: true,
        getDefaultValue: (): boolean | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.TABLETOP:
            case WidgetType.ANF_TABLETOP:
              return true;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.KEEP_DUPLICITIES,
        label: 'Duplicate Products',
        type: FeatureType.BOOL,
        value: FeatureValue.KEEP_DUPLICITIES,
        battle: true,
        getDefaultValue: (): boolean | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.COMPARISON:
              return true;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DEDUPLICATION_STRATEGY,
        label: 'Deduplication Strategy',
        type: FeatureType.LIST,
        value: FeatureValue.DEDUPLICATION_STRATEGY,
        battle: true,
        options: [
          {
            value: 'merchant_id',
            formatted_value: 'Deduplicate based on merchant ID',
          },
          {
            value: 'currency',
            formatted_value: 'Deduplicate based on currency',
          },
          {
            value: 'image',
            formatted_value: 'Deduplicate based on image URL',
          },
        ],
      },
      {
        id: FeatureId.OFFER_EXCL,
        type: FeatureType.TEXT,
        value: FeatureValue.OFFER_EXCL,
      },
      {
        id: FeatureId.OFFER,
        label: 'Offer Filtering',
        type: FeatureType.LIST,
        value: FeatureValue.OFFER,
        battle: true,
        options: [
          {
            value: 'editors_pick_promo',
            formatted_value: "Editor's Pick Products",
          },
          {
            value: 'exclusive_promo',
            formatted_value: 'Exclusive Products',
          },
          {
            value: 'recommended_promo',
            formatted_value: 'Recommended Products',
          },
          {
            value: 'amazon_lightning',
            formatted_value: 'Amazon Lightning Deal Products',
          },
          {
            value: 'amazon_prime',
            formatted_value: 'Amazon Prime Deal Products',
          },
          {
            value: 'deal_text_promo',
            formatted_value: 'Deal Text Products',
          },
        ],
      },
      {
        id: FeatureId.RELATED_MODELS,
        label: 'Related Models',
        type: FeatureType.BOOL,
        value: FeatureValue.RELATED_MODELS,
        battle: true,
      },
      {
        id: FeatureId.PRODUCT_TYPES,
        label: 'Product Types',
        type: FeatureType.LIST,
        value: FeatureValue.FILTER_PRODUCT_TYPES,
        options: [
          {
            value: 'deals',
            formatted_value: 'Only Show Retail Deals',
          },
          {
            value: 'contracts',
            formatted_value: 'Only Show Contract Deals',
          },
          {
            value: 'subscriptions',
            formatted_value: 'Only Show Subscription Deals',
          },
          {
            value: 'broadband',
            formatted_value: 'Only Show Broadband Deals',
          },
          {
            value: 'accessories',
            formatted_value: 'Only Show Accessory Deals',
          },
          {
            value: 'fallback',
            formatted_value: 'Only Show Fallback Deals',
          },
          {
            value: 'offers',
            formatted_value: 'Only Show Offers',
          },
          {
            value: 'vouchers',
            formatted_value: 'Only Show Voucher Deals',
          },
          {
            value: 'offer_deals',
            formatted_value: 'Only Show MVC Deals',
          },
          {
            value: 'similar',
            formatted_value: 'Only Show View Similar Deals',
          },
          {
            value: 'unique_codes',
            formatted_value: 'Only Show Unique Codes Deals',
          },
          {
            value: 'multimedia',
            formatted_value: 'Only Show Multimedia Deals',
          },
          {
            value: 'newsletter',
            formatted_value: 'Only Show Newsletter Deals',
          },
          {
            value: 'rewards',
            formatted_value: 'Only Show Reward Deals',
          },
        ],
        battle: true,
        getDefaultValue: (): string | null => {
          const { attributes, widget, site } = this;
          const defaultValue = ((): string | null => {
            // We set the default here to ensure we send a missing event when
            // no deals are found (we might not display them in the widget component).
            // This should match the setup for the widget - e.g., we should only
            // include product types which we have a component setup for.
            switch (widget.type) {
              case WidgetType.MULTI_MODEL_REVIEW:
              case WidgetType.ANF_MULTIMODEL:
              case WidgetType.PARTNER:
              case WidgetType.PPC:
                return 'contracts,deals,subscriptions';
              case WidgetType.POCKET:
              case WidgetType.PRODUCT_NAME:
                return 'contracts,deals,similar';
              case WidgetType.COLLECTION:
              case WidgetType.ANF_COLLECTION:
              case WidgetType.MAGAZINE_SUBSCRIPTIONS:
                return 'deals';
              case WidgetType.TABLETOP:
              case WidgetType.ANF_TABLETOP:
              case WidgetType.ANF_BUTTON:
              case WidgetType.ANF_FEATURED_PRODUCT_BLOCK:
                return 'deals,subscriptions,similar';
              case WidgetType.BUNDLE:
              case WidgetType.DEAL:
              case WidgetType.ANF_DEAL:
              case WidgetType.TABS:
              case WidgetType.PEACOCK:
              case WidgetType.USER_REVIEWS:
                return 'deals,similar';
              case WidgetType.COMPARISON:
              case WidgetType.LINK:
                return 'broadband,subscriptions,contracts,deals,similar';
              case WidgetType.INBODY:
                return 'broadband,subscriptions,contracts,deals';
              case WidgetType.EDITORS_PICK:
                return 'contracts';
              case WidgetType.PRICE:
                return 'subscriptions,deals,fallback,similar';
              case WidgetType.REVIEW:
              case WidgetType.ANF_REVIEW:
                return 'subscriptions,contracts,deals,fallback,similar';
              case WidgetType.SIMPLE:
                return 'subscriptions,contracts,deals,similar';
              case WidgetType.MERCHANT_LINK:
                switch (site) {
                  case MarieClaireUS:
                  case MarieClaireUK:
                  case WomanAndHome:
                  case Livingetc:
                  case MyImperfectLife:
                    if (attributes.link) {
                      return 'subscriptions,deals';
                    }
                    return 'subscriptions,deals,multimedia,similar';
                  default:
                    return 'subscriptions,deals,multimedia,similar';
                }
              case WidgetType.SPECS:
              case WidgetType.STICKY:
              case WidgetType.PRODUCT_BLOCK:
                if (attributes.link && attributes.linkText) {
                  return 'subscriptions,deals';
                }
                return 'subscriptions,deals,similar';
              case WidgetType.STREAMING:
                return 'subscriptions,deals,multimedia,similar';
              case WidgetType.PROMOTION:
                return 'subscriptions,deals';
              case WidgetType.EAGLE:
              case WidgetType.MERCHANT_OVERVIEW:
              case WidgetType.MOST_POPULAR_OFFERS:
              case WidgetType.TILE:
              case WidgetType.SCROLLABLE_TILE:
                return 'unique_codes,vouchers,offer_deals,newsletter,rewards';
              case WidgetType.BROADBAND:
                return 'broadband';
              default:
                // Not all widgets call widget.php - e.g., seasonal and userrewards
                return null;
            }
          })();

          // Set up filter product types based on dealsOnly & contractsOnly
          // Only apply the filter if the widget supports deals or contracts
          // (prevents the widget from being exploited to show unsupported deals)
          if (
            hasValue(attributes.dealsOnly) &&
            parseBoolean(attributes.dealsOnly) &&
            defaultValue?.includes('deals')
          ) {
            return 'deals';
          }
          if (
            hasValue(attributes.contractsOnly) &&
            parseBoolean(attributes.contractsOnly) &&
            defaultValue?.includes('contracts')
          ) {
            return 'contracts';
          }

          return defaultValue;
        },
      },
      {
        id: FeatureId.ROWS,
        label: 'Rows',
        type: FeatureType.NUMBER,
        value: FeatureValue.ROWS,
        battle: true,
        getDefaultValue: (): number | null => {
          const { widget, attributes } = this;

          switch (widget.type) {
            case WidgetType.PPC:
            case WidgetType.MERCHANT_LINK:
              return 4;
            case WidgetType.ANF_BUTTON:
            case WidgetType.ANF_FEATURED_PRODUCT_BLOCK:
              if (site === WhoWhatWear) {
                return 1;
              }
              return 4;
            case WidgetType.DEAL:
            case WidgetType.ANF_DEAL:
            case WidgetType.EDITORS_PICK:
            case WidgetType.INBODY:
            case WidgetType.LINK:
            case WidgetType.PROMOTION:
            case WidgetType.SPECS:
            case WidgetType.STICKY:
            case WidgetType.USER_REVIEWS:
            case WidgetType.PRODUCT_BLOCK:
              return 1;
            case WidgetType.BUNDLE:
            case WidgetType.MAGAZINE_SUBSCRIPTIONS:
            case WidgetType.PRICE:
            case WidgetType.REVIEW: {
              const { widgetType, name } = attributes;

              if (name === 'TOP widget' && widgetType === WidgetType.REVIEW) {
                return 4;
              }

              if (name === 'BOT widget') {
                return 5;
              }

              return 3;
            }
            case WidgetType.STREAMING:
            case WidgetType.BROADBAND:
              return 3;
            case WidgetType.MOST_POPULAR_OFFERS:
            case WidgetType.PEACOCK:
              return 5;
            case WidgetType.ANF_REVIEW:
            case WidgetType.ANF_MULTIMODEL:
              return 6;
            case WidgetType.SCROLLABLE_TILE:
              return 8;
            case WidgetType.TILE:
              return 9;
            case WidgetType.COMPARISON:
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.PARTNER:
              return 10;
            case WidgetType.EAGLE:
            case WidgetType.MERCHANT_OVERVIEW:
              return 50;
            case WidgetType.TABLETOP:
            case WidgetType.ANF_TABLETOP:
            case WidgetType.COLLECTION:
            case WidgetType.ANF_COLLECTION:
              return 400; // Maximum number allowed by Search API, see WidgetParameterFinder.MAX_ROWS
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.DEVICE,
        type: FeatureType.TEXT,
        value: FeatureValue.DEVICE,
        getDefaultValue: (): string => getDeviceCategory(),
      },
      {
        id: FeatureId.ORIGIN,
        type: FeatureType.TEXT,
        value: FeatureValue.ORIGIN,
        getDefaultValue: (): string => this.origin,
      },
      {
        id: FeatureId.PRODUCT_ID,
        type: FeatureType.TEXT,
        value: FeatureValue.PRODUCT_ID,
      },
      {
        id: FeatureId.ONLY_FALLBACK_OFFERS,
        label: 'Only Fallback Offers',
        type: FeatureType.BOOL,
        value: FeatureValue.ONLY_FALLBACK_OFFERS,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.VERIFIED,
        type: FeatureType.TEXT,
        value: FeatureValue.VERIFIED,
      },
      {
        id: FeatureId.TRANSLATE_PERCENTAGE_SAVING,
        label: 'Translate Percentage Saving',
        type: FeatureType.BOOL,
        value: FeatureValue.TRANSLATE_PERCENTAGE_SAVING,
        battle: true,
      },
      {
        id: FeatureId.USE_NUCLEUS_REWRITE,
        label: 'Use Nucleus Rewrite',
        type: FeatureType.BOOL,
        value: FeatureValue.USE_NUCLEUS_REWRITE,
        battle: true,
      },
      {
        id: FeatureId.USE_DEAL_MERCHANT_LINK_TEXT,
        label: 'Use deal merchant link text',
        type: FeatureType.BOOL,
        value: FeatureValue.USE_DEAL_MERCHANT_LINK_TEXT,
        battle: true,
      },
      {
        id: FeatureId.POST_SORT,
        type: FeatureType.TEXT,
        value: FeatureValue.POST_SORT,
        getDefaultValue: (): string | null => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.TABLETOP:
            case WidgetType.ANF_TABLETOP:
              return 'price';
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.USE_ARS,
        label: 'PPC ARS parameter',
        type: FeatureType.BOOL,
        value: FeatureValue.USE_ARS,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget } = this;
          switch (widget.type) {
            case WidgetType.PPC:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.SOCIAL,
        type: FeatureType.TEXT,
        value: FeatureValue.SOCIAL,
      },
    ];
  }

  /**
   * Return the label API filters, which are dynamic so do not exist in the features array
   */
  // can be any value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLabels(): Record<string, any> {
    const { attributes } = this;
    return getLabelAPIParams(attributes);
  }
}
